import styled from "styled-components";
import type { TilaMuutos } from "../types";
import TilaBadge from "./TilaBadge";
import { formatAikaleima } from "../utils/format";
import { useTranslation } from "react-i18next";
import { TFunction } from "i18next";

/**
 * Tukipyynnön käsittelyhistoria — tilamuutokset aikajärjestyksessä (vanhin
 * ylimpänä). Workshop 1.6.2026 C1: virkailija näkee tukipyynnön elinkaaren
 * — saapunut → käsittelyyn otettu → ratkaistu — aikaleimoineen ja
 * käsittelijöineen. Erillinen sektio ja eri konsepti kuin käyttäjäkortin
 * audit-trail (joka kerää käyttäjään liittyviä virkailijatoimenpiteitä).
 */

interface Props {
  historia: TilaMuutos[];
}

const List = styled.ol`
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
`;

const Row = styled.li`
  display: flex;
  align-items: flex-start;
  gap: ${({ theme }) => theme.spacing.m};
  padding: ${({ theme }) => theme.spacing.s} 0;

  & + & {
    border-top: 1px solid ${({ theme }) => theme.colors.depthLight2};
  }
`;

const Vasen = styled.div`
  flex-shrink: 0;
  min-width: 9rem;
`;

const Aika = styled.div`
  font: ${({ theme }) => theme.typography.bodyTextSmall};
  font-size: 0.8125rem;
  color: ${({ theme }) => theme.colors.depthDark1};
  font-variant-numeric: tabular-nums;
`;

const Oikea = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.xxs};
`;

const Virkailija = styled.div`
  font: ${({ theme }) => theme.typography.bodyTextSmall};
  font-size: 0.8125rem;
  color: ${({ theme }) => theme.colors.blackBase};
`;

const VirkailijaPuuttuu = styled.div`
  font: ${({ theme }) => theme.typography.bodyTextSmall};
  font-size: 0.75rem;
  font-style: italic;
  color: ${({ theme }) => theme.colors.depthDark1};
`;

export default function TukipyyntoHistoria({ historia }: Props) {
  const { t } = useTranslation(["tukipyyntoNakyma", "common"]);
  const reversed = [...historia].reverse();

  return (
    <List>
      {reversed.map((rivi, idx) => {
        const isLastItem = idx === reversed.length - 1;
        const avoinLabel = isLastItem
          ? t("saapunut")
          : t("virkailija_palautti");

        return (
          <Row key={`${rivi.tila}-${rivi.aikaleima}-${idx}`}>
            <Vasen>
              <Aika>{formatAikaleima(rivi.aikaleima)}</Aika>
            </Vasen>
            <Oikea>
              <TilaBadge tila={rivi.tila} />
              {rivi.virkailijaNimi ? (
                <Virkailija>{rivi.virkailijaNimi}</Virkailija>
              ) : rivi.tila === "avoin" ? (
                <VirkailijaPuuttuu>{avoinLabel}</VirkailijaPuuttuu>
              ) : rivi.tila === "self-resolved" ? (
                <VirkailijaPuuttuu>{t("selfresolved")}</VirkailijaPuuttuu>
              ) : null}
            </Oikea>
          </Row>
        );
      })}
    </List>
  );
}
